/**
 * Follows a server-sent event stream: reconnects with exponential backoff (1s, 2s, 4s … 30s), and after two failed
 * connections also polls a snapshot while it waits, so the page stays current if SSE can't get through (a proxy that
 * buffers, say). 401, 403 and 404 stop it: retrying won't help. The run page and the founder's stream share it.
 */
import { ApiError, toApiError } from './api'
import { parseSSE, type SseEvent } from './sse'

export type WatchState = 'connecting' | 'live' | 'reconnecting' | 'polling' | 'stopped'

export interface WatchEventsOptions<T> {
  open: (signal: AbortSignal) => Promise<Response>
  poll: () => Promise<T>
  onEvent: (event: SseEvent) => void
  onPoll: (snapshot: T) => void
  onState?: (state: WatchState, error?: Error) => void
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>
  baseDelayMs?: number
  maxDelayMs?: number
  /** Failed connections in a row before polling starts. */
  pollAfter?: number
  pollEveryMs?: number
}

const FATAL = new Set([401, 403, 404])

export function backoff(attempt: number, base = 1000, max = 30_000): number {
  return Math.min(base * 2 ** Math.max(0, attempt - 1), max)
}

const defaultSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms)
    signal.addEventListener('abort', () => (clearTimeout(t), resolve()), { once: true })
  })

export function watchEvents<T>(o: WatchEventsOptions<T>): { stop: () => void; done: Promise<void> } {
  const ctrl = new AbortController()
  const sleep = o.sleep ?? defaultSleep
  const base = o.baseDelayMs ?? 1000
  const max = o.maxDelayMs ?? 30_000
  const pollAfter = o.pollAfter ?? 2
  const pollEvery = o.pollEveryMs ?? 5000
  const state = (s: WatchState, e?: Error) => o.onState?.(s, e)

  async function pollOnce(): Promise<boolean> {
    try {
      o.onPoll(await o.poll())
      return true
    } catch (e) {
      if (e instanceof ApiError && FATAL.has(e.status)) {
        state('stopped', e)
        return false
      }
      return true
    }
  }

  async function loop() {
    let failures = 0
    state('connecting')
    while (!ctrl.signal.aborted) {
      try {
        const res = await o.open(ctrl.signal)
        if (!res.ok) throw await toApiError(res)
        if (!res.body) throw new Error('the event stream has no body')
        state('live')
        for await (const ev of parseSSE(res.body, ctrl.signal)) {
          failures = 0
          o.onEvent(ev)
        }
      } catch (e) {
        if (ctrl.signal.aborted) break
        if (e instanceof ApiError && FATAL.has(e.status)) {
          state('stopped', e)
          return
        }
      }
      if (ctrl.signal.aborted) break
      failures++
      const delay = backoff(failures, base, max)
      if (failures >= pollAfter) {
        state('polling')
        for (let waited = 0; waited < delay && !ctrl.signal.aborted; waited += pollEvery) {
          if (!(await pollOnce())) return
          await sleep(Math.min(pollEvery, delay - waited), ctrl.signal)
        }
      } else {
        state('reconnecting')
        await sleep(delay, ctrl.signal)
      }
    }
    state('stopped')
  }

  const done = loop()
  return { stop: () => ctrl.abort(), done }
}
